import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  checkDesktopUpdate,
  type DesktopUpdate,
  isDesktop,
  onDeepLink,
  openExternal,
} from "../../lib/desktop";

const UPDATE_CHECK_MS = 6 * 60 * 60_000;

/**
 * Mounted once at the root when running inside the desktop app. Three things a browser tab does
 * for free that the shell has to be told: links to other sites open in the system browser, a
 * `tank://` link delivered to the app navigates inside it, and a newer build is fetched and offered.
 */
export function DesktopBridge() {
  const navigate = useNavigate();
  const [update, setUpdate] = useState<DesktopUpdate | undefined>();
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!isDesktop()) return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin === window.location.origin) return;
      e.preventDefault();
      void openExternal(url.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    if (!isDesktop()) return;
    let off: () => void = () => {};
    let live = true;
    void onDeepLink((path) => void navigate({ to: path as never })).then((unsub) => {
      if (live) off = unsub;
      else unsub();
    });
    return () => {
      live = false;
      off();
    };
  }, [navigate]);

  useEffect(() => {
    if (!isDesktop()) return;
    let live = true;
    const look = () =>
      checkDesktopUpdate()
        .then((u) => live && u && setUpdate(u))
        .catch(() => undefined); // offline, or the feed is down: try again next time
    void look();
    const t = setInterval(look, UPDATE_CHECK_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  if (!update) return null;
  return (
    <Snackbar
      open
      message={`TANK ${update.version} is ready.`}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      action={
        <Button
          size="small"
          color="primary"
          disabled={installing}
          onClick={() => {
            setInstalling(true);
            update.install().catch(() => setInstalling(false));
          }}
          data-testid="desktop-update-install"
        >
          {installing ? "Installing…" : "Restart to update"}
        </Button>
      }
    />
  );
}
